/**
 * Team Skills API — the staffing and skill-coverage surface for team leads and
 * supervisors as specified in GET /api/v1/teams/{team_id}/skills.
 *
 * This endpoint evaluates a team's declared skill demands against its active
 * members and scheduled shifts over a given temporal query window.
 *
 * Distinct from individual profile skills (/me/skills and /users/{id}/skills),
 * this projection aggregates:
 *   - The minimum skill demand policy governing the team (by total holders or per-shift).
 *   - Current team holders and whether they are on shift right now.
 *   - For per-shift requirements, concrete rostered shift slots with any staffing gap.
 *
 * Every block on the wire is required except `shifts`, which is present exclusively
 * when `required.minimum` specifies a `per_shift` requirement. An absent `shifts`
 * block signifies a team-level `holders` demand, whereas an empty array `[]`
 * signifies a `per_shift` demand where no matching shifts are rostered in the query
 * window.
 */

import { z } from 'zod';

import { api, type ReadPage } from '../client';
import { PeriodSchema } from '../temporal';

// ---------------------------------------------------------------------------
// Shared vocabulary
// ---------------------------------------------------------------------------

/**
 * A pointer to a resource, in the shape tricorder-go/reference uses everywhere.
 * Redeclared locally mirroring frontend/src/api/endpoints/skills.ts without
 * importing a private schema from that module.
 */
export const ReferenceSchema = z.object({
  reference: z.string().optional(),
  display: z.string().optional(),
  identifier: z.object({ system: z.string(), value: z.string() }).optional(),
});

export type Reference = z.infer<typeof ReferenceSchema>;

/** Scope of the skill demand policy. */
export const SkillAppliesToSchema = z.object({
  // All three are OPTIONAL because the gateway omits an unset selector
  // (`json:",omitempty"`), and the demand schema requires only that at least
  // ONE of them is set. A tenant-wide rule carries `role` alone and a
  // kind-wide rule carries `team_kind` alone, so requiring all three rejected
  // every real policy document and rendered the board as an error.
  //
  // This is the one place in this module where `.optional()` is right, and it
  // is not the usual trap: the field is absent because the SERVER never sends
  // it, not because a rename made it parse to undefined. Every response block
  // below stays required.
  team_ref: z.string().optional(),
  team_kind: z.string().optional(),
  role: z.string().optional(),
});

export type SkillAppliesTo = z.infer<typeof SkillAppliesToSchema>;

/**
 * The staffing threshold. The wire contract guarantees exactly one of
 * `holders` or `per_shift` is set, never both.
 */
const MinimumHoldersSchema = z.object({
  holders: z.number().int(),
  per_shift: z.undefined().optional(),
});

const MinimumPerShiftSchema = z.object({
  per_shift: z.number().int(),
  holders: z.undefined().optional(),
});

export const SkillMinimumSchema = z.union([MinimumHoldersSchema, MinimumPerShiftSchema]);

export type SkillMinimum = z.infer<typeof SkillMinimumSchema>;

/** The requirement rule applied to the team. */
export const SkillDemandSchema = z.object({
  applies_to: SkillAppliesToSchema,
  skill_id: z.string(),
  shift_type: z.string().nullable().optional(),
  minimum: SkillMinimumSchema,
});

export type SkillDemand = z.infer<typeof SkillDemandSchema>;

/** A member holding the required skill. */
export const TeamSkillHolderSchema = z.object({
  user: ReferenceSchema,
  basis: z.string(),
  // When the holder's qualification is in force. Null on a declaration, which
  // has no validity interval; `end` null on a qualification that does not
  // expire. A period whose `end` has passed is a lapsed credential, which
  // remains a valid and informative fact.
  period: PeriodSchema.nullable(),
  on_shift_now: z.boolean(),
});

export type TeamSkillHolder = z.infer<typeof TeamSkillHolderSchema>;

/** Scheduled shift slot evaluated for per-shift skill coverage. */
export const ShiftCoverageSchema = z.object({
  date: z.string(),
  shift_type: z.string(),
  // Half-open [start, end). A null `end` means the slot is still open: it has
  // begun and nothing says when it finishes, which is what temporal.Period
  // writes for a shift with no planned end. It is a real state, not a missing
  // value.
  period: PeriodSchema,
  holders_present: z.number().int(),
  gap: z.number().int(),
});

export type ShiftCoverage = z.infer<typeof ShiftCoverageSchema>;

/** One skill row on the team's coverage evaluation. */
export const TeamSkillRowSchema = z.object({
  skill: ReferenceSchema,
  title: z.record(z.string(), z.string()),
  required: SkillDemandSchema,
  holder_count: z.number().int(),
  on_shift_now_count: z.number().int(),
  rostered_count: z.number().int(),
  holders: z.array(TeamSkillHolderSchema),
  // Modelled as optional ONLY because shifts is present on a per_shift minimum,
  // and absent on a holders minimum. Absent and empty are different answers:
  // empty says the window rosters no shift of the demanded type.
  shifts: z.array(ShiftCoverageSchema).optional(),
});

export type TeamSkillRow = z.infer<typeof TeamSkillRowSchema>;

/** Envelope returned by GET /teams/{team_id}/skills. */
export const TeamSkillsResponseSchema = z.object({
  items: z.array(TeamSkillRowSchema),
  subject: ReferenceSchema,
  period: z.string(),
});

export type TeamSkillsResponse = z.infer<typeof TeamSkillsResponseSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extracts the minimum demand value and discriminating kind.
 */
export function demandMinimum(
  required: SkillDemand,
): { kind: 'holders' | 'per_shift'; value: number } {
  if (required.minimum.holders !== undefined) {
    return { kind: 'holders', value: required.minimum.holders };
  }
  return { kind: 'per_shift', value: required.minimum.per_shift };
}

// ---------------------------------------------------------------------------
// Queries and API client
// ---------------------------------------------------------------------------

function listQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value !== undefined) query.set(name, String(value));
  }
  const qs = query.toString();
  return qs === '' ? '' : `?${qs}`;
}

export interface GetTeamSkillsParams extends Record<string, string | number | undefined> {
  period?: string;
  limit?: number;
  offset?: number;
}

/**
 * Fetch skill coverage and shift staffing gaps for a team.
 *
 * The server enforces permissions, answering 403 ACCESS_DENIED when the caller
 * lacks management rights over the team, and 400 INVALID_REQUEST for malformed period queries.
 */
export async function getTeamSkills(
  teamId: string,
  params: GetTeamSkillsParams = {},
): Promise<ReadPage<TeamSkillsResponse>> {
  return api.getPage(`/teams/${encodeURIComponent(teamId)}/skills${listQuery(params)}`, {
    schema: TeamSkillsResponseSchema,
  });
}
